import patientsServices from '@/services/patients.services.js';
import prescriptionsServices from '@/services/prescriptions.services.js';
const root = { root: true };
let detailRequest = 0;
let listRequest = 0;
const LIST_CONFIG = {
    encounters: { getter: 'records/encounters', service: (patientId, cursor) => patientsServices.getEncounters(patientId, cursor), set: 'setEncounters' },
    vaccinations: { getter: 'records/vaccinations', service: (patientId, cursor) => patientsServices.getVaccinations(patientId, cursor), set: 'setVaccinations' },
    labExams: { getter: 'records/labExams', service: (patientId, cursor) => patientsServices.getLabExams(patientId, cursor), set: 'setLabExams' },
    hospitalizations: { getter: 'records/hospitalizations', service: (patientId, cursor) => patientsServices.getHospitalizations(patientId, cursor), set: 'setHospitalizations' },
    prescriptions: { getter: 'records/prescriptions', service: (patientId, cursor) => prescriptionsServices.getPrescriptions(patientId, cursor, { pagination: 'v2' }), set: 'setPrescriptions' }
};

const normalizeListPage = response => {
    if (Array.isArray(response)) return { items: response, nextCursor: null, hasMore: false };
    return {
        items: Array.isArray(response && response.items) ? response.items : [],
        nextCursor: response && response.nextCursor ? response.nextCursor : null,
        hasMore: !!(response && response.hasMore)
    };
};
const mergeById = (current, incoming) => {
    const byId = new Map((current || []).map(item => [item.id, item]));
    (incoming || []).forEach(item => {
        if (!item || !item.id) return;
        byId.set(item.id, { ...(byId.get(item.id) || {}), ...item });
    });
    return Array.from(byId.values());
};

/**
 * `general/loadingHandler` nunca relanca a excecao e, quando recebe `errorAction`,
 * deixa de chamar o `general/showError` padrao. As actions de save do prontuario
 * precisam do erro de volta (o `saveAll` monta um alerta unico com as secoes que
 * falharam), entao o alerta padrao so e suprimido quando quem chama pede
 * explicitamente `showError: false`. Assim telas que ignoram o retorno, como o
 * Ddx, continuam avisando o usuario quando o save falha.
 */
const saveErrorAction = (dispatch, showError) => err => {
    if (showError) dispatch('general/showError', null, root);
    return { ok: false, error: err };
};

/**
 * As listas do prontuario sao carregadas por paciente. Se o usuario trocar de
 * paciente enquanto uma requisicao esta em voo, a resposta antiga nao pode
 * sobrescrever a lista do paciente novo: o commit so acontece quando o paciente
 * selecionado ainda e o mesmo de quando a requisicao comecou.
 */
const isCurrentPatient = (rootGetters, patientId) => {
    const current = rootGetters['patients/patient'];
    return !!current && current.id === patientId;
};
const isCurrentEncounter = (state, encounterId) => !!state.selectedEncounter && state.selectedEncounter.id === encounterId;
const contentRevisionFor = (state, encounterId) => isCurrentEncounter(state, encounterId) ? (state.selectedEncounter.contentRevision || 0) : undefined;
const revisionPayload = record => ({ ...record, ...(record.id ? { expectedRevision: record.revision || 0 } : {}) });

/**
 * O backend nao devolve no save os campos que o cliente nunca envia
 * (`fileUrls` no exame, `evolutions` na internacao): eles sao preservados por
 * merge no Firestore, mas ficam de fora da resposta. Sem este merge local o
 * card perderia a lista de anexos ou de evolucoes ate a proxima leitura.
 * @param {object|null} previous item que ja estava na lista
 * @param {object} saved resposta do servidor
 * @param {string[]} preservedFields
 */
const mergeServerRecord = (previous, saved, preservedFields = []) => {
    if (!previous || !saved) return saved;
    const merged = { ...saved };
    for (const field of preservedFields) {
        if (merged[field] === undefined && previous[field] !== undefined) {
            merged[field] = previous[field];
        }
    }
    return merged;
};

/**
 * Substitui o item salvo na lista, ou o insere no topo quando e novo.
 */
const upsertRecord = (current, saved, preservedFields = []) => {
    const list = current || [];
    const previous = list.find(item => item.id === saved.id);
    const merged = mergeServerRecord(previous, saved, preservedFields);
    if (previous) return list.map(item => (item.id === saved.id ? merged : item));
    return [merged, ...list];
};

/**
 * O contrato garante apenas `evolution` na resposta de `POST .../evolutions`:
 * a lista completa (`evolutions`) vem hoje como cortesia do backend, mas nao
 * pode ser a unica fonte. Sem o append local o card ficaria igual depois de
 * salvar e o veterinario registraria a mesma evolucao de novo.
 * @param {Array} previous evolucoes que ja estavam no card
 * @param {object} result resposta do servidor
 */
const mergeAddedEvolution = (previous, result) => {
    if (result && Array.isArray(result.evolutions)) return result.evolutions;
    const list = previous || [];
    if (result && result.evolution) return [...list, result.evolution];
    return list;
};

/**
 * Mesmo raciocinio do upload de laudo: o contrato garante o arquivo recem
 * enviado, e a lista completa `fileUrls` e apenas um atalho. Sem o append o
 * anexo sumiria da tela e seria reenviado, duplicando o laudo.
 * @param {Array} previous anexos que ja estavam no exame
 * @param {object} result resposta do servidor
 */
const mergeUploadedFile = (previous, result) => {
    if (result && Array.isArray(result.fileUrls)) return result.fileUrls;
    const list = previous || [];
    if (result && result.file) return [...list, result.file];
    return list;
};

export default {
    // One context commit: an old/failed request must never leave another patient's
    // encounter visible. Components decide how to present or retry the failure.
    fetchEncounterContext: async ({ commit, rootGetters }, { patientId, encounterId }) => {
        const request = ++detailRequest;
        commit('setSelectedEncounter', null);
        commit('setAnamnesis', null);
        commit('setPhysicalExam', null);
        commit('setDiagnoses', []);
        try {
            const [encounter, anamnesis, physicalExam, diagnoses] = await Promise.all([
                patientsServices.getEncounterById(encounterId),
                patientsServices.getAnamnesis(encounterId),
                patientsServices.getPhysicalExam(encounterId),
                patientsServices.getDiagnoses(encounterId)
            ]);
            if (request !== detailRequest || !isCurrentPatient(rootGetters, patientId)) return { ok: false, stale: true };
            if (!encounter || encounter.patientId !== patientId) throw new Error('Consulta não pertence ao paciente selecionado.');
            commit('setSelectedEncounter', encounter);
            commit('setAnamnesis', anamnesis);
            commit('setPhysicalExam', physicalExam);
            commit('setDiagnoses', diagnoses);
            return { ok: true, data: encounter };
        } catch (error) { return { ok: false, error }; }
    },
    fetchTimelineType: async ({ commit, state, rootGetters, dispatch }, { type, append = false } = {}) => {
        const config = LIST_CONFIG[type];
        const patient = rootGetters['patients/patient'];
        const page = state.pagination[type];
        if (!config || !patient || !patient.id || !page || (append && (!page.hasMore || page.loading))) return;
        const generation = ++listRequest;
        commit('setPagination', { type, patch: { loading: true, error: null, generation, ...(append ? {} : { cursor: null, hasMore: true }) } });
        await dispatch('general/loadingHandler', {
            logEventArgs: `fetch${type}`,
            action: async () => {
                const response = await config.service(patient.id, append ? page.cursor : null);
                const latest = state.pagination[type];
                if (!isCurrentPatient(rootGetters, patient.id) || latest.generation !== generation) return;
                const result = normalizeListPage(response);
                const records = append ? mergeById(rootGetters[config.getter], result.items) : mergeById([], result.items);
                commit(config.set, records);
                commit('setPagination', { type, patch: { cursor: result.nextCursor, hasMore: result.hasMore, loading: false, error: null } });
                commit('setTruncated', { type, value: result.hasMore });
            },
            errorAction(error) {
                if (isCurrentPatient(rootGetters, patient.id) && state.pagination[type].generation === generation) {
                    commit('setPagination', { type, patch: { loading: false, error: error || true } });
                }
            }
        }, root);
        if (isCurrentPatient(rootGetters, patient.id) && state.pagination[type].generation === generation) commit('setPagination', { type, patch: { loading: false } });
    },
    fetchEncounters: ({ dispatch }, { append = false } = {}) => {
        return dispatch('fetchTimelineType', { type: 'encounters', append });
    },
    fetchEncounter: async ({ commit, dispatch }, encounterId) => {
        if (!encounterId) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'fetchEncounter',
            action: async () => {
                const encounter = await patientsServices.getEncounterById(encounterId);
                commit('setSelectedEncounter', encounter);
            }
        }, root);
    },
    createEncounter: async ({ dispatch, rootGetters, commit }) => {
        const patient = rootGetters['patients/patient'];
        if (!patient || !patient.id) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'createEncounter',
            action: async () => {
                const encounter = await patientsServices.saveEncounter({
                    patientId: patient.id,
                    ownerId: patient.ownerId,
                    type: 0,
                    status: 0,
                    chiefComplaint: '',
                    notes: ''
                });
                commit('setSelectedEncounter', encounter);
                dispatch('general/routePathRedirect', {
                    path: `/patients/${patient.id}/encounters/${encounter.id}`
                }, root);
            }
        }, root);
    },
    /**
     * Salva a consulta. O `general/loadingHandler` nunca relanca a excecao, entao o
     * resultado do erro chega por `errorAction` e a action devolve sempre
     * `{ ok, data }` ou `{ ok: false, error }` para quem chamou poder decidir.
     */
    saveEncounter: async ({ dispatch, commit, state }, { encounter, showMessage = true, showError = true } = {}) => {
        if (!encounter) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'saveEncounter',
            action: async () => {
                const saved = await patientsServices.saveEncounter(encounter);
                if (isCurrentEncounter(state, encounter.id)) commit('setSelectedEncounter', saved);
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    /**
     * Adendo de registro finalizado. O contrato exige o body exatamente
     * `{ id, addendum: { text } }`; qualquer campo extra recebe HTTP 409.
     */
    addEncounterAddendum: async ({ dispatch, commit }, { encounterId, text, showError = true } = {}) => {
        if (!encounterId || !text) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'addEncounterAddendum',
            action: async () => {
                const saved = await patientsServices.saveEncounter({
                    id: encounterId,
                    addendum: { text }
                });
                commit('setSelectedEncounter', saved);
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError)
        }, root);
    },
    fetchAnamnesis: async ({ commit, dispatch }, encounterId) => {
        if (!encounterId) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'fetchAnamnesis',
            action: async () => {
                const anamnesis = await patientsServices.getAnamnesis(encounterId);
                commit('setAnamnesis', anamnesis);
            }
        }, root);
    },
    saveAnamnesis: async ({ commit, dispatch, state }, { encounterId, anamnesis, showMessage = true, showError = true } = {}) => {
        if (!encounterId) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'saveAnamnesis',
            action: async () => {
                const saved = await patientsServices.saveAnamnesis(encounterId, { ...anamnesis, expectedContentRevision: contentRevisionFor(state, encounterId) });
                const { contentRevision, ...data } = saved;
                if (isCurrentEncounter(state, encounterId)) { commit('setAnamnesis', data); commit('setEncounterContentRevision', { encounterId, contentRevision }); }
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    fetchPhysicalExam: async ({ commit, dispatch }, encounterId) => {
        if (!encounterId) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'fetchPhysicalExam',
            action: async () => {
                const physicalExam = await patientsServices.getPhysicalExam(encounterId);
                commit('setPhysicalExam', physicalExam);
            }
        }, root);
    },
    savePhysicalExam: async ({ commit, dispatch, state }, { encounterId, physicalExam, showMessage = true, showError = true } = {}) => {
        if (!encounterId) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'savePhysicalExam',
            action: async () => {
                const saved = await patientsServices.savePhysicalExam(encounterId, { ...physicalExam, expectedContentRevision: contentRevisionFor(state, encounterId) });
                const { contentRevision, ...data } = saved;
                if (isCurrentEncounter(state, encounterId)) { commit('setPhysicalExam', data); commit('setEncounterContentRevision', { encounterId, contentRevision }); }
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    fetchDiagnoses: async ({ commit, dispatch }, encounterId) => {
        if (!encounterId) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'fetchDiagnoses',
            action: async () => {
                const diagnoses = await patientsServices.getDiagnoses(encounterId);
                commit('setDiagnoses', diagnoses);
            }
        }, root);
    },
    saveDiagnoses: async ({ commit, dispatch, state }, { encounterId, diagnoses, showMessage = true, showError = true } = {}) => {
        if (!encounterId) return { ok: false, error: null };
        // Diagnóstico não é obrigatório para guardar uma consulta em rascunho.
        // Remoções de itens existentes são enviadas como tombstones pelo formulário.
        if (Array.isArray(diagnoses) && diagnoses.length === 0 && !(state.diagnoses || []).length) {
            return { ok: true, data: [] };
        }
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'saveDiagnoses',
            action: async () => {
                const saved = await patientsServices.saveDiagnoses(encounterId, { items: diagnoses, expectedContentRevision: contentRevisionFor(state, encounterId) });
                const items = Array.isArray(saved) ? saved : saved.items;
                if (isCurrentEncounter(state, encounterId)) { commit('setDiagnoses', items); commit('setEncounterContentRevision', { encounterId, contentRevision: saved.contentRevision }); }
                return { ok: true, data: items };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    fetchVaccinations: ({ dispatch }, { append = false } = {}) => {
        return dispatch('fetchTimelineType', { type: 'vaccinations', append });
    },
    /**
     * Salva a vacina. Com `finalize: true` o backend assina o registro; a tela
     * precisa do erro de volta para traduzir 400 (campo obrigatorio ausente),
     * 403 (sem CRMV) e 409 (ja finalizada).
     */
    saveVaccination: async ({ dispatch, commit, rootGetters }, { vaccination, finalize = false, showMessage = true, showError = true } = {}) => {
        if (!vaccination) return { ok: false, error: null };
        const patient = rootGetters['patients/patient'];
        return await dispatch('general/loadingHandler', {
            logEventArgs: finalize ? 'finalizeVaccination' : 'saveVaccination',
            action: async () => {
                const saved = await patientsServices.saveVaccination({
                    ...revisionPayload(vaccination),
                    patientId: vaccination.patientId || (patient && patient.id),
                    finalize: finalize || undefined
                });
                if (isCurrentPatient(rootGetters, saved.patientId)) commit('setVaccinations', upsertRecord(rootGetters['records/vaccinations'], saved));
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    deleteVaccination: async ({ dispatch, commit, rootGetters }, vaccinationId) => {
        if (!vaccinationId) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'deleteVaccination',
            action: async () => {
                await patientsServices.deleteVaccination(vaccinationId);
                const current = rootGetters['records/vaccinations'] || [];
                commit('setVaccinations', current.filter(v => v.id !== vaccinationId));
                return { ok: true };
            },
            errorAction: saveErrorAction(dispatch, false)
        }, root);
    },
    fetchVaccinationSchedule: async ({ commit, dispatch, rootGetters }, patientId) => {
        if (!patientId) return;
        await dispatch('general/loadingHandler', {
            logEventArgs: 'fetchVaccinationSchedule',
            action: async () => {
                const schedule = await patientsServices.getVaccinationSchedule(patientId);
                if (isCurrentPatient(rootGetters, patientId)) commit('setVaccinationSchedule', schedule);
            }
        }, root);
    },
    fetchLabExams: ({ dispatch }, { append = false } = {}) => {
        return dispatch('fetchTimelineType', { type: 'labExams', append });
    },
    saveLabExam: async ({ dispatch, commit, rootGetters }, { labExam, finalize = false, showMessage = true, showError = true } = {}) => {
        if (!labExam) return { ok: false, error: null };
        const patient = rootGetters['patients/patient'];
        return await dispatch('general/loadingHandler', {
            logEventArgs: finalize ? 'finalizeLabExam' : 'saveLabExam',
            action: async () => {
                const saved = await patientsServices.saveLabExam({
                    ...revisionPayload(labExam),
                    patientId: labExam.patientId || (patient && patient.id),
                    finalize: finalize || undefined
                });
                if (isCurrentPatient(rootGetters, saved.patientId)) commit('setLabExams', upsertRecord(rootGetters['records/labExams'], saved, ['fileUrls']));
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    deleteLabExam: async ({ dispatch, commit, rootGetters }, labExamId) => {
        if (!labExamId) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'deleteLabExam',
            action: async () => {
                await patientsServices.deleteLabExam(labExamId);
                const current = rootGetters['records/labExams'] || [];
                commit('setLabExams', current.filter(e => e.id !== labExamId));
                return { ok: true };
            },
            errorAction: saveErrorAction(dispatch, false)
        }, root);
    },
    /**
     * Anexa um laudo ao exame. A resposta traz a lista completa de arquivos ja
     * com a URL assinada de 15 minutos, que so vale para esta sessao: ela fica
     * no estado em memoria e nunca e persistida.
     */
    uploadLabExamFile: async ({ dispatch, commit, rootGetters }, { labExamId, file, fileName, contentType } = {}) => {
        if (!labExamId || !file) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'uploadLabExamFile',
            action: async () => {
                const result = await patientsServices.uploadLabExamFile(labExamId, { file, fileName, contentType });
                const current = rootGetters['records/labExams'] || [];
                commit('setLabExams', current.map(e => (
                    e.id === labExamId ? { ...e, fileUrls: mergeUploadedFile(e.fileUrls, result) } : e
                )));
                return { ok: true, data: result };
            },
            errorAction: saveErrorAction(dispatch, false)
        }, root);
    },
    fetchHospitalizations: ({ dispatch }, { append = false } = {}) => {
        return dispatch('fetchTimelineType', { type: 'hospitalizations', append });
    },
    /**
     * Salva a internacao. `finalize: true` e a alta: o contrato exige
     * `dischargeDate`, `dischargeCondition` e `dischargeSummary` no mesmo body.
     */
    saveHospitalization: async ({ dispatch, commit, rootGetters }, { hospitalization, finalize = false, showMessage = true, showError = true } = {}) => {
        if (!hospitalization) return { ok: false, error: null };
        const patient = rootGetters['patients/patient'];
        return await dispatch('general/loadingHandler', {
            logEventArgs: finalize ? 'dischargeHospitalization' : 'saveHospitalization',
            action: async () => {
                const saved = await patientsServices.saveHospitalization({
                    ...revisionPayload(hospitalization),
                    patientId: hospitalization.patientId || (patient && patient.id),
                    finalize: finalize || undefined
                });
                if (isCurrentPatient(rootGetters, saved.patientId)) commit('setHospitalizations', upsertRecord(rootGetters['records/hospitalizations'], saved, ['evolutions']));
                return { ok: true, data: saved };
            },
            errorAction: saveErrorAction(dispatch, showError),
            showMessage
        }, root);
    },
    deleteHospitalization: async ({ dispatch, commit, rootGetters }, hospitalizationId) => {
        if (!hospitalizationId) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'deleteHospitalization',
            action: async () => {
                await patientsServices.deleteHospitalization(hospitalizationId);
                const current = rootGetters['records/hospitalizations'] || [];
                commit('setHospitalizations', current.filter(h => h.id !== hospitalizationId));
                return { ok: true };
            },
            errorAction: saveErrorAction(dispatch, false)
        }, root);
    },
    /**
     * Evolucao da internacao. O backend exige `notes` e recusa (409) depois da
     * alta, entao a tela precisa do erro para explicar o motivo.
     */
    addEvolution: async ({ dispatch, commit, rootGetters }, { hospitalizationId, evolution } = {}) => {
        if (!hospitalizationId || !evolution) return { ok: false, error: null };
        return await dispatch('general/loadingHandler', {
            logEventArgs: 'addEvolution',
            action: async () => {
                const result = await patientsServices.addEvolution(hospitalizationId, evolution);
                const current = rootGetters['records/hospitalizations'] || [];
                commit('setHospitalizations', current.map(h => {
                    if (h.id === hospitalizationId) {
                        return { ...h, evolutions: mergeAddedEvolution(h.evolutions, result) };
                    }
                    return h;
                }));
                return { ok: true, data: result };
            },
            errorAction: saveErrorAction(dispatch, false)
        }, root);
    },
    /**
     * Prescricoes do paciente na linha do tempo. Usa o mesmo endpoint da aba de
     * prescricoes (`GET /patients/prescriptions?patientId=`), cuja pagina e menor.
     */
    fetchPrescriptions: ({ dispatch }, { append = false } = {}) => {
        return dispatch('fetchTimelineType', { type: 'prescriptions', append });
    },
    loadMoreTimeline: async ({ dispatch, state }) => {
        const types = Object.keys(LIST_CONFIG).filter(type => {
            const page = state.pagination[type];
            return page && page.hasMore && !page.loading;
        });
        return await Promise.all(types.map(type => dispatch('fetchTimelineType', { type, append: true })));
    },
    setTimelineFilter: ({ commit }, filter) => {
        commit('setTimelineFilter', filter);
    },
    toggleHideDrafts: ({ commit, state }) => {
        commit('setHideDrafts', !state.hideDrafts);
    },
    clearRecords: ({ commit }) => {
        commit('clearRecords');
    }
};
